import React from "react";
import { FaBell, FaTimes, FaTrash } from "react-icons/fa";

/**
 * Slide-in notification panel that shows all received push notifications.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - notifications: Array<{ id, title, body, data, timestamp, read }>
 *  - onMarkRead: (id) => void
 *  - onMarkAllRead: () => void
 *  - onClear: () => void
 *  - onNotificationTap: (notification) => void  — called when a notification row is tapped
 */
export default function NotificationPanel({
  isOpen,
  onClose,
  notifications = [],
  onMarkRead,
  onMarkAllRead,
  onClear,
  onNotificationTap,
}) {
  const unreadCount = notifications.filter((n) => !n.read).length;

  function formatTime(ts) {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-[70]"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-sm bg-white z-[80] shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white p-4 safe-top">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaBell size={18} />
              <h2 className="text-lg font-semibold">Notifications</h2>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* Actions */}
          {notifications.length > 0 && (
            <div className="flex gap-3 mt-3 text-xs">
              {unreadCount > 0 && (
                <button
                  onClick={onMarkAllRead}
                  className="bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={onClear}
                className="bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg flex items-center gap-1"
              >
                <FaTrash size={10} /> Clear all
              </button>
            </div>
          )}
        </div>

        {/* Notification List */}
        <div className="overflow-y-auto" style={{ height: "calc(100% - 120px)" }}>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <FaBell size={48} className="mb-4 opacity-30" />
              <p className="text-lg font-medium">No notifications</p>
              <p className="text-sm mt-1">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => {
                    onMarkRead && onMarkRead(n.id);
                    if (onNotificationTap) {
                      onNotificationTap(n);
                      onClose && onClose();
                    }
                  }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                    !n.read ? "bg-blue-50/50 border-l-4 border-blue-500" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-sm ${
                          !n.read ? "font-semibold text-gray-900" : "font-medium text-gray-700"
                        }`}
                      >
                        {n.title || "Notification"}
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                        {n.body}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTime(n.timestamp)}
                      </p>
                    </div>
                    {!n.read && (
                      <span className="mt-1 w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
